import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import useModal from "hooks/modals/useModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { StatusCode } from "api/protocols";
import { DeleteScheduledReport, GetScheduledReports } from "api/rpc/2024-04/facilityAdmin/report/scheduledReport";
import { IReport } from "api/rpc/2024-04/facilityAdmin/report/report";

import { showError } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import { valueToString } from "helpers/Helpers";
import { faPencil, faTrash } from "@fortawesome/pro-regular-svg-icons";

import Page from "components/page/Page";
import DataTable from "../../../../components/dataTable/DataTable";
import Popup from "components/popup/Popup";
import Portal from "elements/Portal";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import ReportScheduleDrawer, {
  IReportScheduleDrawerEditTarget,
} from "components/reportsList/ReportScheduleDrawer";

import { IUser } from "redux/reducers/models/user";

import "./reports.scss";

export interface IScheduledReport {
  id: number | string;
  facility_id: number | string;
  schedule: string;
  time: string;
  day_of_week: string;
  report: IReport;
  admin_user: IUser;
}

type TScheduleDrawerState =
  | { mode: "create" }
  | { mode: "edit"; editTarget: IReportScheduleDrawerEditTarget };

export default function ScheduledReports() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [scheduledReports, setScheduledReports] = useState<IScheduledReport[]>(undefined);
  const [scheduleDrawer, setScheduleDrawer] = useState<TScheduleDrawerState | null>(null);

  const {
    state: deleteScheduledReportPopup,
    updateModal: updateDeleteScheduledReportPopup,
    closeModal: closeDeleteScheduledReportPopup,
  } = useModal<{ id: number | string | null }>({
    id: null,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadScheduledReports(source.token);
    return () => source.cancel();
  }, []);

  async function loadScheduledReports(token?: CancelToken) {
    const res = await GetScheduledReports(null, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading scheduled reports")));
      return;
    }

    setScheduledReports(res.data as IScheduledReport[]);
  }

  async function removeScheduledReport(): Promise<void> {
    if (deleteScheduledReportPopup.id == null) {
      return;
    }

    const res = await DeleteScheduledReport({ id: Number(deleteScheduledReportPopup.id) }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.message === "string" ? res.message : t("Error deleting scheduled report")));
      return;
    }

    void loadScheduledReports();
    closeDeleteScheduledReportPopup();
  }

  const handleOpenCreateDrawer = useCallback(() => {
    setScheduleDrawer({ mode: "create" });
  }, []);

  const handleOpenEditDrawer = useCallback((report: IScheduledReport) => {
    setScheduleDrawer({
      mode: "edit",
      editTarget: {
        id: report.id,
        reportId: report.report?.id,
        reportTitle: report.report?.title,
        adminId: report.admin_user?.id,
        schedule: report.schedule,
        dayOfWeek: report.day_of_week,
      },
    });
  }, []);

  const handleCloseScheduleDrawer = useCallback(() => {
    setScheduleDrawer(null);
  }, []);

  const handleScheduleDrawerSuccess = useCallback((): void => {
    void loadScheduledReports();
  }, []);

  const handleRemoveScheduledReport = useCallback((): void => {
    void removeScheduledReport();
  }, [deleteScheduledReportPopup.id]);

  const primaryAction = {
    content: t("Create Scheduled Report"),
    action: handleOpenCreateDrawer,
  };

  return (
    <Page
      title={t("Scheduled Reports")}
      breadcrumbs={[{ prefix: true, label: t("Reports"), url: "/admin/report" }]}
      primaryAction={primaryAction}
    >
      {scheduledReports === undefined || scheduledReports.length > 0 ? (
        <DataTable
          columns={[
            { label: t("Report") },
            { label: t("Schedule") },
            { label: t("Day") },
            { label: t("Admin") },
            { label: t("Actions") },
          ]}
          loading={scheduledReports === undefined}
        >
          {scheduledReports?.map((report, index: number) => (
            <tr key={index}>
              <td>{report.report?.title}</td>
              <td>{valueToString(report.schedule)}</td>
              <td>{valueToString(report.day_of_week)}</td>
              <td>
                <div>{report.admin_user?.full_name}</div>
                <div>{report.admin_user.email}</div>
              </td>
              <td className="clickable">
                <FontAwesomeIcon className="mr-1" icon={faPencil} onClick={() => handleOpenEditDrawer(report)} />
                <FontAwesomeIcon
                  icon={faTrash}
                  onClick={() => updateDeleteScheduledReportPopup({ isOpen: true, id: report.id })}
                />
              </td>
            </tr>
          ))}
        </DataTable>
      ) : (
        <MessageDisplay
          icon="magnifying-glass"
          title={t("No Scheduled Reports")}
          content={t("No scheduled reports were found. Would you like to create one?")}
          actions={[
            {
              label: t("Create Scheduled Report"),
              action: handleOpenCreateDrawer,
            },
          ]}
        />
      )}

      <ReportScheduleDrawer
        open={scheduleDrawer != null}
        showReportSelect={scheduleDrawer?.mode === "create"}
        editTarget={scheduleDrawer?.mode === "edit" ? scheduleDrawer.editTarget : null}
        onClose={handleCloseScheduleDrawer}
        onSuccess={handleScheduleDrawerSuccess}
      />

      <Portal isMounted={deleteScheduledReportPopup.isOpen}>
        <Popup
          type="warning"
          open={deleteScheduledReportPopup.isOpen}
          title={t("Delete scheduled report")}
          description={t("Are you sure you want to delete this scheduled report?")}
          closable
          onCancel={closeDeleteScheduledReportPopup}
          okText={t("Delete")}
          onOk={handleRemoveScheduledReport}
        />
      </Portal>
    </Page>
  );
}
